import { ModalContentCollection } from './ModalContentCollection';
import type { ModalContentItem } from './types';
import type { ResolvedModalDisplayOptions } from '../options';

/**
 * // Class Obj: モーダルに表示中の汎用コンテンツの位置インジケーターを表示
 * // Note:
 * // - item 単位のインジケーターは、現在 group 内で「何番目 / 全何件」かを表示します。
 * // - group 単位のインジケーターは、現在 group が「何番目 / 全何グループ」かを表示します。
 * // - create: true の場合は、表示用DOMをTS側で自動生成します。
 * // - data-modal-indicator 系の手書きマークアップがある場合は、自動生成より優先します。
 */
export class ModalContentIndicator
{
  private static readonly SELECTOR_CONTENT_DISPLAY = '[data-modal-content-display]';
  private static readonly SELECTOR_OUTSIDE_CONTAINER = '.window';

  private static readonly SELECTOR_ITEM_INDICATOR = '[data-modal-indicator]';
  private static readonly SELECTOR_ITEM_INDICATOR_CURRENT = '[data-modal-indicator-current]';
  private static readonly SELECTOR_ITEM_INDICATOR_TOTAL = '[data-modal-indicator-total]';
  private static readonly SELECTOR_GENERATED_ITEM_INDICATOR = '[data-modal-indicator][data-modal-indicator-generated="true"]';

  private static readonly SELECTOR_GROUP_INDICATOR = '[data-modal-indicator-group]';
  private static readonly SELECTOR_GROUP_INDICATOR_CURRENT = '[data-modal-indicator-group-current]';
  private static readonly SELECTOR_GROUP_INDICATOR_TOTAL = '[data-modal-indicator-group-total]';
  private static readonly SELECTOR_GENERATED_GROUP_INDICATOR = '[data-modal-indicator-group][data-modal-indicator-generated="true"]';

  private readonly mContentCollection: ModalContentCollection;
  private readonly mOptions: ResolvedModalDisplayOptions['indicator'];

  /**
   * // Method: 位置インジケーターに必要な依存クラスと設定を受け取る
   */
  constructor(
    content_collection: ModalContentCollection,
    options: ResolvedModalDisplayOptions['indicator'],
  )
  {
    this.mContentCollection = content_collection;
    this.mOptions = options;
  }


  /**
   * // Method: 自動生成したインジケーターDOMを削除する
   */
  public destroy(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((target_modal: HTMLElement) =>
    {
      const generated_indicators: HTMLElement[] = [
        ...this.getElementsBySelector(
          target_modal,
          ModalContentIndicator.SELECTOR_GENERATED_ITEM_INDICATOR,
        ),
        ...this.getElementsBySelector(
          target_modal,
          ModalContentIndicator.SELECTOR_GENERATED_GROUP_INDICATOR,
        ),
      ];

      generated_indicators.forEach((indicator_element: HTMLElement) =>
      {
        indicator_element.remove();
      });
    });
  }

  /**
   * // Method: 現在表示中の item に合わせて位置インジケーターを更新する
   * // Note:
   * // - item_index の値そのものではなく、sort 済み配列内での順番を表示します。
   * // - そのため data-modal-content-index が 0 始まりでも、表示は 1 始まりになります。
   */
  public update(
    target_modal: HTMLElement | null,
    content_item: ModalContentItem,
  ): void
  {
    if (!this.mOptions.enabled)
    {
      return;
    }

    if (!target_modal)
    {
      return;
    }

    this.ensureIndicatorElements(target_modal);
    this.applyPlacement(target_modal);

    if (this.mOptions.item)
    {
      this.updateItemIndicator(target_modal, content_item);
    }

    if (this.mOptions.group)
    {
      this.updateGroupIndicator(target_modal, content_item);
    }
  }

  /**
   * // Method: option に応じて位置インジケーター用DOMを自動生成する
   */
  private ensureIndicatorElements(target_modal: HTMLElement): void
  {
    if (!this.mOptions.create)
    {
      return;
    }

    if (this.mOptions.item)
    {
      this.ensureGeneratedIndicator(
        target_modal,
        ModalContentIndicator.SELECTOR_ITEM_INDICATOR,
        ModalContentIndicator.SELECTOR_GENERATED_ITEM_INDICATOR,
        'item',
      );
    }

    if (this.mOptions.group)
    {
      this.ensureGeneratedIndicator(
        target_modal,
        ModalContentIndicator.SELECTOR_GROUP_INDICATOR,
        ModalContentIndicator.SELECTOR_GENERATED_GROUP_INDICATOR,
        'group',
      );
    }
  }

  /**
   * // Method: 指定種別の自動生成インジケーターを用意する
   * // Note:
   * // - 手書きマークアップが存在する場合は、自動生成せず既存要素を優先します。
   * // - placement が切り替わった場合は、自動生成要素だけを適切な親へ移動します。
   */
  private ensureGeneratedIndicator(
    target_modal: HTMLElement,
    indicator_selector: string,
    generated_indicator_selector: string,
    indicator_type: 'item' | 'group',
  ): void
  {
    const manual_indicator: HTMLElement | null = this.getManualIndicator(
      target_modal,
      indicator_selector,
      generated_indicator_selector,
    );

    if (manual_indicator)
    {
      return;
    }

    const indicator_container: HTMLElement = this.getIndicatorContainer(target_modal);
    let generated_indicator: HTMLElement | null = target_modal.querySelector<HTMLElement>(generated_indicator_selector);

    if (!generated_indicator)
    {
      generated_indicator = this.createIndicatorElement(indicator_type);
    }

    if (generated_indicator.parentElement !== indicator_container)
    {
      indicator_container.appendChild(generated_indicator);
    }
  }

  /**
   * // Method: 手書きインジケーターを取得する
   */
  private getManualIndicator(
    target_modal: HTMLElement,
    indicator_selector: string,
    generated_indicator_selector: string,
  ): HTMLElement | null
  {
    const indicator_elements: HTMLElement[] = this.getElementsBySelector(target_modal, indicator_selector);

    return indicator_elements.find((indicator_element: HTMLElement) =>
      !indicator_element.matches(generated_indicator_selector),
    ) ?? null;
  }

  /**
   * // Method: 自動生成インジケーターの追加先を取得する
   */
  private getIndicatorContainer(target_modal: HTMLElement): HTMLElement
  {
    if (this.mOptions.placement === 'outside')
    {
      return target_modal.querySelector<HTMLElement>(ModalContentIndicator.SELECTOR_OUTSIDE_CONTAINER) ?? target_modal;
    }

    return target_modal.querySelector<HTMLElement>(ModalContentIndicator.SELECTOR_CONTENT_DISPLAY) ?? target_modal;
  }

  /**
   * // Method: 自動生成インジケーター要素を作成する
   */
  private createIndicatorElement(indicator_type: 'item' | 'group'): HTMLElement
  {
    const indicator_element: HTMLElement = document.createElement('div');
    const current_element: HTMLElement = document.createElement('span');
    const separator_element: HTMLElement = document.createElement('span');
    const total_element: HTMLElement = document.createElement('span');

    indicator_element.className = 'modal-indicator';
    indicator_element.hidden = true;
    indicator_element.setAttribute('data-modal-indicator-generated', 'true');

    if (indicator_type === 'group')
    {
      indicator_element.setAttribute('data-modal-indicator-group', '');
      current_element.setAttribute('data-modal-indicator-group-current', '');
      total_element.setAttribute('data-modal-indicator-group-total', '');
    }
    else
    {
      indicator_element.setAttribute('data-modal-indicator', '');
      current_element.setAttribute('data-modal-indicator-current', '');
      total_element.setAttribute('data-modal-indicator-total', '');
    }

    separator_element.setAttribute('aria-hidden', 'true');
    separator_element.textContent = ' / ';

    indicator_element.append(current_element, separator_element, total_element);

    return indicator_element;
  }

  /**
   * // Method: option の placement をインジケーター要素へ反映する
   */
  private applyPlacement(target_modal: HTMLElement): void
  {
    const indicator_elements: HTMLElement[] = [
      ...this.getElementsBySelector(target_modal, ModalContentIndicator.SELECTOR_ITEM_INDICATOR),
      ...this.getElementsBySelector(target_modal, ModalContentIndicator.SELECTOR_GROUP_INDICATOR),
    ];

    indicator_elements.forEach((indicator_element: HTMLElement) =>
    {
      indicator_element.setAttribute('data-modal-indicator-placement', this.mOptions.placement);
    });
  }

  /**
   * // Method: item 単位の位置インジケーターを更新する
   */
  private updateItemIndicator(
    target_modal: HTMLElement,
    content_item: ModalContentItem,
  ): void
  {
    const current_position: number | null = this.mContentCollection.getItemPositionInGroup(content_item);

    if (current_position === null)
    {
      return;
    }

    const current_count: number = current_position + 1;
    const total_count: number = this.mContentCollection.getItemCountByGroup(content_item.group_name);

    this.updateIndicatorElements(
      target_modal,
      ModalContentIndicator.SELECTOR_ITEM_INDICATOR,
      ModalContentIndicator.SELECTOR_ITEM_INDICATOR_CURRENT,
      ModalContentIndicator.SELECTOR_ITEM_INDICATOR_TOTAL,
      current_count,
      total_count,
      '現在の項目位置',
    );
  }

  /**
   * // Method: group 単位の位置インジケーターを更新する
   */
  private updateGroupIndicator(
    target_modal: HTMLElement,
    content_item: ModalContentItem,
  ): void
  {
    const current_position: number | null = this.mContentCollection.getGroupPosition(content_item.group_name);

    if (current_position === null)
    {
      return;
    }

    const current_count: number = current_position + 1;
    const total_count: number = this.mContentCollection.getGroupCount();

    this.updateIndicatorElements(
      target_modal,
      ModalContentIndicator.SELECTOR_GROUP_INDICATOR,
      ModalContentIndicator.SELECTOR_GROUP_INDICATOR_CURRENT,
      ModalContentIndicator.SELECTOR_GROUP_INDICATOR_TOTAL,
      current_count,
      total_count,
      '現在のグループ位置',
    );
  }

  /**
   * // Method: 位置インジケーター用の各要素へ数値を反映する
   * // Note:
   * // - current / total 用の子要素がある場合は、それぞれへ数値だけを入れます。
   * // - 子要素がない場合は、親要素へ `1 / 5` のような文字列を直接入れます。
   */
  private updateIndicatorElements(
    target_modal: HTMLElement,
    indicator_selector: string,
    current_selector: string,
    total_selector: string,
    current_count: number,
    total_count: number,
    label: string,
  ): void
  {
    const indicator_elements: HTMLElement[] = this.getElementsBySelector(target_modal, indicator_selector);

    indicator_elements.forEach((indicator_element: HTMLElement) =>
    {
      const current_element: HTMLElement | null = indicator_element.querySelector<HTMLElement>(current_selector);
      const total_element: HTMLElement | null = indicator_element.querySelector<HTMLElement>(total_selector);

      if (current_element)
      {
        current_element.textContent = String(current_count);
      }

      if (total_element)
      {
        total_element.textContent = String(total_count);
      }

      indicator_element.hidden = false;
      indicator_element.setAttribute('aria-label', `${label}: ${current_count} / ${total_count}`);

      if (current_element || total_element)
      {
        return;
      }

      indicator_element.textContent = `${current_count} / ${total_count}`;
    });
  }

  /**
   * // Method: 指定モーダル内から位置インジケーター用の要素を取得する
   */
  private getElementsBySelector(target_modal: HTMLElement, selector: string): HTMLElement[]
  {
    return Array.from(target_modal.querySelectorAll<HTMLElement>(selector));
  }
}
